<template>
  <div>
    <v-container fluid grid-list-xl px-0 py-2>
      <v-layout row wrap>
        <v-flex sm12 md7>
          <checkout-form
            :form="checkoutForm"
            :errors="errors"
            :is_pickup_available="cartDetails.is_pickup_available"
          />
        </v-flex>
        <v-flex sm12 md5>
          <div>
            <base-card class="pa-4" :class="{'loading': loading}" color="accent">
              <h1 class="title font-weight-bold">{{ $t('GIFT_CARD.YOUR_ORDER') }}</h1>
              <v-divider class="mt-3" />
              <!-- List of artworks -->
              <div class="my-4 pb-2">
                <v-layout row wrap v-for="artwork in cartDetails.artworks" :key="artwork.id">
                  <v-flex xs6 class="title font-weight-regular">{{ artwork.title }}</v-flex>
                  <v-flex xs6 class="title font-weight-regular" text-xs-right>{{ artwork.price }} €</v-flex>
                </v-layout>
              </div>
              <!-- /List of artworks -->

              <v-layout row wrap mt-2 mb-2>
                <!-- Discount -->
                <template v-if="cartDetails.discount_coupon">
                  <v-flex xs6 class="title font-weight-regular">{{ $t('CART.DISCOUNT') }}</v-flex>
                  <v-flex xs6 class="title font-weight-regular" text-xs-right>
                    <span>{{ cartDetails.discount_coupon.discount_rate }}</span>
                    <span class="ml-1">({{ cartDetails.discount_coupon.discount_amount }})</span>
                  </v-flex>
                </template>
                <!-- Giftcard -->
                <template v-if="cartDetails.gift_card">
                  <v-flex xs6 class="title font-weight-regular">{{ $t('MENU.GIFT_CARD') }}</v-flex>
                  <v-flex
                    xs6
                    class="title font-weight-regular"
                    text-xs-right
                  >{{ cartDetails.gift_card.amount }}</v-flex>
                </template>
                <!-- Shipping cost -->
                <v-flex xs12>
                  <shipping-cost :cartDetails="cartDetails" />
                </v-flex>
              </v-layout>

              <!-- Total sum -->
              <total-sum :cartDetails="cartDetails" />

              <tk-btn
                block
                large
                color="primary"
                class="my-5"
                :loading="pending"
                @click="checkout"
              >{{ $t('CART.PURCHASE') }}</tk-btn>

              <div class="action-btns">
                <v-list dense>
                  <v-list-tile @click="showPageDialog('delivery')">
                    <v-list-tile-title class="text-uppercase">{{ $t('ARTWORK.DELIVERY') }}</v-list-tile-title>
                  </v-list-tile>
                  <v-divider />
                  <v-list-tile @click="showPageDialog('payment')">
                    <v-list-tile-title class="text-uppercase">{{ $t('ARTWORK.PAYMENT') }}</v-list-tile-title>
                  </v-list-tile>
                  <v-divider />
                  <v-list-tile @click="showPageDialog('returns')">
                    <v-list-tile-title class="text-uppercase">{{ $t('ARTWORK.RETURNS') }}</v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </div>
            </base-card>

            <!-- Show payment options cards -->
            <tk-payment-options class="mt-4" />
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { ErrorHandler } from "@/utils/ErrorHandler";
import { useGTag } from '../../services/google/google-tag-manager'

const handler = new ErrorHandler();

export default {
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (!vm.$cart.country.id) {
        next({ name: "cart" });
      }
    });
  },

  components: {
    CheckoutForm: () => import("./CheckoutForm"),
    ShippingCost: () => import("../Cart/ShippingCost"),
    TotalSum: () => import("../Cart/TotalSum"),
    TkPaymentOptions: () => import("@/components/core/TkPaymentOptions"),
  },

  data() {
    return {
      checkoutForm: {
        shipping: { country_id: this.$cart.country.id },
        billing: { country_id: this.$cart.country.id },
        // endpoints requires default value
        ship_to_another_address: false,
        newsletter: false,
        register: false,
      },
      errors: { billing: {}, shipping: {} },
      loading: false,
      pending: false,
      cartDetails: {
        artworks: [],
        delivery: { amount: "-" },
        total: { amount: "-", amount_value: null },
      },
    };
  },

  created() {
    this.fetchData();
  },

  methods: {
    showPageDialog(slug) {
      this.$app.pageDialog = { show: true, slug: slug };
    },

    getCartPayload() {
      const payload = {
        artworks: this.$cart.items.map((item) => item.id),
        is_business: this.$cart.isBusiness,
        country_id: this.$cart.country.id,
      };

      // Conditionally add codes
      if (this.$cart.discount_coupon_code) {
        payload.discount_coupon = this.$cart.discount_coupon_code;
      }
      if (this.$cart.gift_card_code) {
        payload.gift_card = this.$cart.gift_card_code;
      }
      if (this.$cart.delivery_code) {
        payload.delivery_code = this.$cart.delivery_code;
      }

      return payload;
    },

    async fetchData() {
      this.loading = true;

      await handler.handle(async () => {
        const res = await this.$axios.post(
          `/cart/details?checkout=1`,
          this.getCartPayload()
        );
        this.cartDetails = res.data["data"];
        console.log("CART DETAILS", this.cartDetails);
      });
      this.loading = false;
    },

    async checkout() {

      this.errors = { billing: {}, shipping: {} }; // Reset errors

      const payload = { ...this.getCartPayload(), ...this.checkoutForm };

      this.pending = true;

      try {
        if (window.ga) {
          ga(() => {
            const trackers = ga.getAll();
            payload.ga_client_id = trackers[0].get("clientId");
          });
        }

        const res = await this.$axios.post("/checkout/artwork", payload);
        const data = res.data.data;
        const url = data.url;

        useGTag().addPaymentInfoFromCart(this.cartDetails);
        useGTag().addShippingInfo(this.cartDetails, this.checkoutForm);

        window.location.href = url;

      } catch (error) {
        if (error.status === 422 && error.data) {
          this.errors = error.data.errors;
        }
        if (window.bugsnagClient) {
          window.bugsnagClient.notify(new Error(JSON.stringify(error)));
        }
        throw error
      } finally {
        this.pending = false;
      }

    },
  },
};
</script>
